import * as React from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import glamorous from 'glamorous';
import styled from '@emotion/styled';
import { ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import LoadingOverlay from '../LoadingOverlay';
import classNames from 'classnames';
import withTranslate, { WithTranslateProps } from '../WithTranslate';
import { Button, Intent, Tag, Tooltip, Position } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import TranslatedMessage from '../translation/TranslatedMessage';
import { TLCode } from '../../../common/enums/TLCode';
import Baby from 'babyparse';
import XLSX from 'xlsx';

const FileSaver = require('file-saver');

// tslint:disable-next-line:no-empty-interface
export interface AgGridContext extends WithTranslateProps {}

// tslint:disable-next-line:no-empty-interface
interface RenderGridProps extends GridOptions {}

// tslint:disable-next-line:no-any
type SafeGridApiInterface = (grid: GridApi) => any;
type SafeGridColumnApiInterface = (grid: ColumnApi) => any;

const AgGridContainer = glamorous.div({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
});

const HeaderContainer = glamorous.div({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 5,
});

const LeftContainer = glamorous.div({
  display: 'flex',
});

export interface BaseGridProps extends AgGridReactProps {
  className?: string;
  loading?: boolean;
  keyName: string;
  height?: string;
  isVisibleInDom?: boolean;
  children?: (props: {
    renderGrid: (props?: RenderGridProps) => React.ReactElement<AgGridReact>;
    callGridApi: (fn: SafeGridApiInterface) => any;
    callGridColumnApi: (fn: SafeGridColumnApiInterface) => any;
  }) => React.ReactNode;
  exportName?: string;
  customExport?: (data: any) => void;
  defaultSort?: { colId: string; sort: 'desc' | 'asc' | 'none' }[];
}

interface BaseGridState {
  rowData: Array<{}>;
}

class BaseGridXL extends React.Component<BaseGridProps & WithTranslateProps, BaseGridState> {
  gridApi: GridApi;
  gridColumnApi: ColumnApi;

  state = {
    rowData: [{}],
    grid: null,
  };

  componentDidUpdate(prevProps: BaseGridProps, prevState: BaseGridState) {
    if (prevProps.loading !== this.props.loading) {
      if (this.props.loading) {
        this.safeGridApi((gridApi) => gridApi.showLoadingOverlay());
      } else {
        this.safeGridApi((gridApi) => gridApi.hideOverlay());
      }
    }

    this.safeGridApi((gridApi) => gridApi.sizeColumnsToFit());
  }

  static getDerivedStateFromProps(nextProps: BaseGridProps, prevState: BaseGridState): Partial<BaseGridState> | null {
    if (nextProps.rowData === undefined || nextProps.rowData! === prevState.rowData || nextProps.loading) {
      return null;
    }
    return {
      rowData: nextProps.rowData!.map((e) => ({ ...e })),
    };
  }

  getRowNodeId = (data: {}) => {
    const { keyName } = this.props;
    return data[keyName!];
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    if (this.props.loading) {
      this.gridApi.showLoadingOverlay();
    }

    if (this.props.defaultSort !== undefined) this.gridApi.setSortModel(this.props.defaultSort);

    params.api.sizeColumnsToFit();
  };

  safeGridColumnApi = (fn: SafeGridColumnApiInterface): any => {
    const gridColumnApi: ColumnApi = this.gridColumnApi;
    if (gridColumnApi) {
      return fn(gridColumnApi);
    }
  };

  safeGridApi = (fn: SafeGridApiInterface): any => {
    const gridApi: GridApi = this.gridApi;
    if (gridApi) {
      return fn(gridApi);
    }
  };

  get rowCount() {
    return (this.props.rowData || []).length;
  }

  export2Excel() {
    if (this.gridApi) {
      this.gridApi.selectAllFiltered();
      if (this.props.customExport != undefined) {
        this.props.customExport(this.parseCsv(this.gridApi.getDataAsCsv()));
      } else {
        this.download2Excel(this.parseCsv(this.gridApi.getDataAsCsv()));
      }
      this.gridApi.deselectAll();
    }
  }

  download2Excel(sheet) {
    const wb = XLSX.utils.book_new();
    wb.SheetNames[0] = 'sheet';
    wb.Sheets.sheet = sheet;
    XLSX.writeFile(wb, this.props.exportName ? this.props.exportName + '.xlsx' : 'Pharmagarde.xlsx');
  }

  parseCsv(csvString) {
    csvString = csvString.replace(/\|/g, '').replaceAll('T00:00:00', '');
    const json = Baby.parse(csvString, { header: true });
    const sheet = XLSX.utils.json_to_sheet(json.data);
    return sheet;
  }

  getRowCount() {
    if (this.gridApi) {
      return this.gridApi.getDisplayedRowCount();
    }
    return 0;
  }

  render() {
    const defaultColDef = {
      editable: false,
      suppressDragLeaveHidesColumns: true,
      sortable: true,
      filter: true,
    };

    const { children, translate } = this.props;
    const renderGrid = (props: RenderGridProps = {}) => {
      const context: AgGridContext = {
        translate,
      };

      const grid = (
        <AgGridReact
          defaultColDef={defaultColDef}
          deltaRowDataMode={true}
          getRowNodeId={this.getRowNodeId}
          loadingOverlayComponentFramework={LoadingOverlay}
          onGridReady={this.onGridReady}
          rowSelection="multiple"
          suppressCellSelection={true}
          suppressMovableColumns={true}
          suppressNoRowsOverlay={true}
          context={context}
          {...this.props}
          {...props}
          rowData={this.state.rowData}
          gridOptions={{ enableBrowserTooltips: true }}
        />
      );

      return <AgGridContainer>{grid}</AgGridContainer>;
    };

    return (
      <div
        className={classNames(this.props.className, 'ag-theme-balham').toString()}
        style={{
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          height: this.props.height ? this.props.height : '100%',
        }}
      >
        <HeaderContainer>
          <LeftContainer>
            <Tag>
              <TranslatedMessage id={TLCode.GRID_NUMBER_RESULT} values={{ nbResult: this.rowCount }} />
            </Tag>
            &nbsp;
            <Tooltip content={this.props.translate(TLCode.GRID_EXCEL_EXPORT)} position={Position.RIGHT}>
              <Button
                type={'submit'}
                intent={Intent.PRIMARY}
                icon={IconNames.TH}
                disabled={this.rowCount === 0}
                small={true}
                onClick={() => this.export2Excel()}
                large
              />
            </Tooltip>
          </LeftContainer>
        </HeaderContainer>
        {children && typeof children === 'function'
          ? children({
              renderGrid,
              callGridApi: this.safeGridApi,
              callGridColumnApi: this.safeGridColumnApi,
            })
          : renderGrid()}
      </div>
    );
  }
}

export default withTranslate(BaseGridXL);
